import { useState } from 'react';
import type { ReactNode } from 'react';
import { getMasterTestingKey } from '../lib/masterTestingKey';
import { SCREENS } from '../lib/screenRegistry';
export const MASTER_ADMIN_CONSOLE_TITLE = 'SCREEN-MPL-11: CLASSROOM MASTER ADMIN CONSOLE';

export type MasterAdminSidebarItem =
  | { kind: 'section'; label: string }
  | { kind: 'external'; label: string; href: string };

export type MasterAdminTab =
  | 'Learning Program Knowledge Hub'
  | 'Students'
  | 'Organisations'
  | 'Learning Modules'
  | 'Key Generator'
  | 'System Data'
  | 'Maintenance Preview'
  | 'Stripe Dashboard';

export const MASTER_ADMIN_SIDEBAR_NAV: MasterAdminSidebarItem[] = [
  { kind: 'section', label: 'Learning Program Knowledge Hub' },
  { kind: 'section', label: 'Students' },
  { kind: 'section', label: 'Organisations' },
  { kind: 'section', label: 'Learning Modules' },
  { kind: 'section', label: 'Key Generator' },
  { kind: 'section', label: 'System Data' },
  { kind: 'section', label: 'Maintenance Preview' },
  { kind: 'external', label: 'Stripe Dashboard', href: 'https://dashboard.stripe.com' },
];

interface MasterAdminShellProps {
  activeSection: MasterAdminTab;
  onSectionChange: (section: MasterAdminTab) => void;
  panelClassName?: string;
  children: ReactNode;
}

export default function MasterAdminShell({
  activeSection,
  onSectionChange,
  panelClassName = '',
  children,
}: MasterAdminShellProps) {
  const masterTestKey = getMasterTestingKey();
  const [copyNotice, setCopyNotice] = useState('');

  const handleCopyTestKey = async () => {
    try {
      await navigator.clipboard.writeText(masterTestKey);
      setCopyNotice('Copied to clipboard.');
    } catch {
      setCopyNotice('Copy failed — select the key and copy manually.');
    }
  };

  return (
    <div className="admin-layout master-admin-layout" data-screen-id={SCREENS.MPL11}>
      <aside className="admin-sidebar master-admin-sidebar">
        <img
          src={encodeURI('/Assets/logo/mentology- logo.png')}
          alt="Mentology"
          className="master-sidebar-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="master-admin-sidebar-heading-block">
          <h1 className="master-admin-console-title">{MASTER_ADMIN_CONSOLE_TITLE}</h1>
          <div className="master-admin-test-key-block">
            <p className="master-admin-test-key-label">Master Test Key</p>
            <div className="master-admin-test-key-row">
              <code className="master-admin-test-key-value window-a-key">{masterTestKey}</code>
              <button
                type="button"
                className="master-admin-test-key-copy-btn"
                onClick={() => { void handleCopyTestKey(); }}
                title="Copy test key to clipboard"
                aria-label="Copy master test key to clipboard"
              >
                Copy
              </button>
            </div>
            {copyNotice ? (
              <p className="master-admin-test-key-copy-notice" role="status">{copyNotice}</p>
            ) : null}
          </div>
        </div>
        <nav className="master-admin-sidebar-nav" aria-label="Master admin sections">
          {MASTER_ADMIN_SIDEBAR_NAV.map((item) => {
            if (item.kind === 'external') {
              return (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-nav-item admin-nav-external-link"
                >
                  {item.label}
                </a>
              );
            }

            return (
              <button
                key={item.label}
                type="button"
                className={`admin-nav-item ${activeSection === item.label ? 'active' : ''}`}
                onClick={() => onSectionChange(item.label as MasterAdminTab)}
              >
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>
      <main className={`admin-panel${panelClassName ? ` ${panelClassName}` : ''}`}>
        {children}
      </main>
    </div>
  );
}
